// ============================================================
// ThemeProvider Siverix — dark par défaut, toggle persisté localStorage
// Cf. docs/08_design_system.md
// ============================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { themes, DEFAULT_THEME, type ThemeName, type ThemeColors } from '@siverix/shared';

const STORAGE_KEY = 'siverix:theme';

interface ThemeContextValue {
  theme: ThemeName;
  colors: ThemeColors;
  setTheme: (theme: ThemeName) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredTheme(): ThemeName {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // localStorage indispo (mode privé, etc.)
  }
  return DEFAULT_THEME;
}

function applyThemeToDocument(theme: ThemeName, colors: ThemeColors): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  const root = document.documentElement.style;
  root.setProperty('--color-bg', colors.bg);
  root.setProperty('--color-surface', colors.surface);
  root.setProperty('--color-surface-hover', colors.surfaceHover);
  root.setProperty('--color-text', colors.text);
  root.setProperty('--color-text-muted', colors.textMuted);
  root.setProperty('--color-border', colors.border);
  root.setProperty('--color-accent', colors.accent);
  root.setProperty('--color-accent-hover', colors.accentHover);
  root.setProperty('--color-success', colors.success);
  root.setProperty('--color-warn', colors.warn);
  root.setProperty('--color-error', colors.error);
}

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [theme, setThemeState] = useState<ThemeName>(() => readStoredTheme());

  const setTheme = useCallback((next: ThemeName) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  useEffect(() => {
    applyThemeToDocument(theme, themes[theme]);
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, colors: themes[theme], setTheme, toggle }),
    [theme, setTheme, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme() doit être utilisé dans un <ThemeProvider>');
  return ctx;
}
